import { createAction, PayloadAction } from "@reduxjs/toolkit";
import { ActionReducerMapBuilder, unImmer } from "../../../common/redux/createReducer";
import { InteropStore, LinkerStore, LinkerClient, LinkerChannel, ClientAdded, ClientRemoved } from "../types";
import { concatNoDups, InteropJoinChannels, InteropLeaveChannels } from "../../../clients/Interop/types";
import resolver from "./resolver";
import { StoreType } from "../store";
import { setComponentState, getComponentState, uuidsFromWindowName, notifyEndUser } from "./commonUtils";
import RouterClient from "../../../clients/routerClient";
import Logger from "../../../clients/logger";
import testPoint from "../../../common/testPoint";
import { isEqualIgnoringArrayOrder } from "../../../common/util";

const { clientAdded, clientRemoved, joinChannels, leaveChannels } = resolver.actions;

// ------------- Types

export type LinkerChannelConfig = {
	name: string;
	color: string;
	border?: string;
	label?: string;
}[];

export type InteropLinkChannels = {
	type: "linker/linkChannels";
	payload: {
		channels: string[];
	};
	meta: {
		windowName: string;
	};
};

export type InteropUnlinkChannels = {
	type: "linker/unlinkChannels";
	payload: {
		channels?: string[];
	};
	meta: {
		windowName: string;
	};
};

export type InteropLinkerSetActiveWindow = {
	type: "linker/setActiveWindow";
	payload: {
		windowName: string;
		showWindowAtMouse?: boolean;
	};
};

type LinkerInitializeWindow = {
	channels?: string[];
	windowName: string;
};

// ------- Helper functions

const getLinkerState = async (windowName: string): Promise<LinkerInitializeWindow> => {
	const data = await getComponentState({ field: "Interop-#Linker-linkedChannels", windowName });
	return {
		channels: data,
		windowName,
	};
};

// Returns array of linker channels that are used (i.e. has one or more clients joined).
const getUsedChannels = (clients: Record<string, LinkerClient>): string[] => {
	let usedChannels: Record<string, boolean> = {};
	Object.values(clients).forEach((client: any) => client.joinedChannels.forEach((used) => (usedChannels[used] = true)));
	return Object.keys(usedChannels);
};

// Returns a free channel or null if they are all used.
const getFreeChannel = (allChannels: LinkerChannel[], clients: Record<string, LinkerClient>): string | null => {
	const usedChannels: string[] = getUsedChannels(clients);
	let filteredChannels = allChannels.filter((channel) => !usedChannels.includes(channel.name));
	return filteredChannels.length > 0 ? filteredChannels[0].name : null;
};

// Removes connectIds from map that no longer have any associated clients (i.e. the connectId's channel is no longer used by any client).
const cleanupConnectIdMap = (linkerStore: LinkerStore) => {
	const usedChannels = getUsedChannels(linkerStore.clients);
	Object.keys(linkerStore.connectIdToJoinedChannel).forEach((connectId) => {
		if (!usedChannels.includes(linkerStore.connectIdToJoinedChannel[connectId])) {
			Logger.system.debug(
				`linker.cleanupConnectIdMap removed ${connectId} for ${linkerStore.connectIdToJoinedChannel[connectId]}`,
				usedChannels
			);
			delete linkerStore.connectIdToJoinedChannel[connectId];
		}
	});
};

/**
 * Citrix windows show the linker window at the mouse position because the windows
 * aren't tracked by finsemble.  This effect queries the launcher to do just that.
 */
const showLinkerWindowAtMouseEffect = (): void => {
	Logger.system.debug("showLinkerWindowAtMouseEffect");
	const params = {
		windowIdentifier: {
			componentType: "linkerWindow",
		},
	};
	RouterClient.query(`Launcher.showWindowAtMouse`, params, (err, queryResponseMessage) => {});
};

const actions = {
	setActiveWindow: createAction<InteropLinkerSetActiveWindow>("linker/setActiveWindow"),
	initializeWindow: createAction<LinkerInitializeWindow>("linker/initializeWindow"),
	linkChannels: createAction<InteropLinkChannels>("linker/linkChannels"),
	joinChannels: createAction<InteropJoinChannels>("resolver/joinChannels"),
	leaveChannels: createAction<InteropLeaveChannels>("resolver/leaveChannels"),
	unlinkChannels: createAction<InteropUnlinkChannels>("linker/unlinkChannels"),
	setSystemChannels: createAction<void>("linker/setSystemChannels"),
};

const initialize = (store: StoreType) => {
	store.dispatch(actions.setSystemChannels());
};

/**
 * The Linker UI components use CSS to drive their colors (using `className`). The initial values here
 * exist only for the benefit of FDC3 clients wishing to use the getSystemChannels()
 * call. These colors cannot be customized.
 *
 * Customers can also use CSS if they really, really need to reduce the number of channels, or if they really, really need
 * to change the channel names. See linkerWindow.css for commented examples of how to do this.
 */
const getInitialState = (): LinkerStore => {
	return {
		clients: {},
		allChannels: [
			{
				name: "Channel 1",
				color: "#8781BD",
				label: "1",
				cssClassName: "channel1",
			},
			{
				name: "Channel 2",
				color: "#FFE035",
				label: "2",
				cssClassName: "channel2",
			},
			{
				name: "Channel 3",
				color: "#89D803",
				label: "3",
				cssClassName: "channel3",
			},
			{
				name: "Channel 4",
				color: "#FE6262",
				label: "4",
				cssClassName: "channel4",
			},
			{
				name: "Channel 5",
				color: "#2DACFF",
				label: "5",
				cssClassName: "channel5",
			},
			{
				name: "Channel 6",
				color: "#FFA200",
				label: "6",
				cssClassName: "channel6",
			},
			{
				name: "Channel 7",
				color: "#3C1D5F",
				label: "7",
				cssClassName: "channel7",
			},
			{
				name: "Channel 8",
				color: "#690F2D",
				label: "8",
				cssClassName: "channel8",
			},
		],
		connectIdToJoinedChannel: {},
	};
};

const addReducerCases = (builder: ActionReducerMapBuilder<InteropStore>) => {
	builder
		.addCase(actions.setActiveWindow, (state, action: PayloadAction<InteropLinkerSetActiveWindow>, cmd) => {
			const fsa = action.payload;
			const { windowName, showWindowAtMouse } = fsa.payload;
			state.linker.activeWindowName = windowName;
			if (showWindowAtMouse) {
				cmd?.run(showLinkerWindowAtMouseEffect);
			}
		})
		.addCase(actions.setSystemChannels, (state) => {
			const { allChannels } = state.linker;
			const { channelMeta } = state.resolver;
			allChannels.forEach((linkerChannel) => {
				channelMeta[linkerChannel.name] = {
					type: "app",
					displayMetaData: {
						name: linkerChannel.name,
						color: linkerChannel.color,
					},
				};
			});
		})
		.addCase(actions.initializeWindow, (state, action: PayloadAction<LinkerInitializeWindow>, cmd) => {
			const { windowName } = action.payload;
			const { spawnedFromWorkspace, connectId } = state.linker.clients[windowName];
			let channels = action.payload.channels ?? [];

			// If connectId is specified, then process it here, auto-joining channels as need. If spawned from workspace can ignore though since workspace remembers joined channels
			if (connectId && !spawnedFromWorkspace) {
				// if a channel is already allocated to the connectId, then use it; However, if no channel allocated, then get one here
				const connectIdChannel =
					state.linker.connectIdToJoinedChannel[connectId] ??
					getFreeChannel(state.linker.allChannels, state.linker.clients);

				// if channel found then add it to the client's channels
				if (connectIdChannel) {
					Logger.system.debug("Linker: freeChannel", connectIdChannel);
					state.linker.connectIdToJoinedChannel[connectId] = connectIdChannel;
					channels.push(connectIdChannel);
					Logger.system.debug("Linker: joinedChannels", windowName, connectIdChannel, channels);
					// update clients before testPoint is triggered
					state.linker.clients[windowName].joinedChannels = channels;
					testPoint.triggerSnapshot("LinkerConnectResults", {
						connectId,
						connectIdChannel,
						clients: unImmer(state.linker.clients),
					});
				} else {
					// no free channel was found so generate user notification and log an error
					const messageTitle = "Out of FDC3 Linker Channels";
					const messageDetails = `Channels could not be auto-associated for ${windowName} because all linker channels are already in use.`;
					cmd?.run(notifyEndUser, {
						args: [messageTitle, messageDetails],
					});
					Logger.system.error(
						messageTitle,
						state.linker.allChannels,
						state.linker.clients,
						state.linker.connectIdToJoinedChannel
					);
				}
			}

			const shouldUpdate = !isEqualIgnoringArrayOrder(channels, state.linker.clients[windowName]?.joinedChannels);

			if (channels?.length) {
				state.linker.clients[windowName].joinedChannels = channels;
				uuidsFromWindowName({ clients: state.resolver.clients, windowName }).forEach((uuid) => {
					state.resolver.clients[uuid].joinedChannels = channels;
				});
			}

			// if a client's channels have changed, save to component state for next time workspace is reloaded
			if (shouldUpdate) {
				cmd?.run(setComponentState, {
					args: [{ field: "Interop-#Linker-linkedChannels", value: unImmer(channels), windowName }],
				});
			}
		})
		// Piggyback on resolver's joinChannels action by linking channels here -- outside of window initialization, this is the only place channel state is directly linked in this module.
		// Firing actions in two different modules is built into the redux code. All of the reducers for an action (joinChannels in this case) are triggered in the order in which they were registered.
		.addCase(joinChannels, (state, action: PayloadAction<InteropJoinChannels>, cmd) => {
			const fsa = action.payload;
			const { windowName } = state.resolver.clients[fsa.meta.uuid];
			const { channels: channelsToAdd } = fsa.payload;

			// there should always be a linker.clients entry for the window name because only addedClients will have joinChannels invoked, but this conditional simplifies some unit testing
			if (state.linker.clients[windowName]) {
				const oldLinkedChannels = state.linker.clients[windowName]?.joinedChannels ?? [];
				const newLinkedChannels = (state.linker.clients[windowName].joinedChannels = concatNoDups(
					oldLinkedChannels,
					channelsToAdd
				));

				testPoint.triggerSnapshot("LinkerJoinChannelsResult", { clients: unImmer(state.linker.clients) });

				cmd?.run(setComponentState, {
					args: [{ field: "Interop-#Linker-linkedChannels", value: unImmer(newLinkedChannels), windowName }],
				});
			} else {
				Logger.system.error(`Linker.joinChannels: state.linker.clients entry not found for ${windowName}`);
			}
		})
		// Here channels are linked by the end user. The affect must be the same as when channels are joined though FDC3 API, so invoke joinChannels.
		// Invoking joinChannels, will trigger two actions: 1) the joinChannels in this module immediately above; 2) the joinChannels in the resolver module.
		.addCase(actions.linkChannels, (state, action: PayloadAction<InteropLinkChannels>, cmd) => {
			const fsa = action.payload;
			const { windowName } = fsa.meta;
			const { channels: channelsToAdd } = fsa.payload;

			// Typically just one uuid per window name, but keeping code flexible for future.
			uuidsFromWindowName({ clients: state.resolver.clients, windowName }).forEach((uuid) => {
				const fsaInteropJoinChannels: InteropJoinChannels = {
					type: "joinChannels",
					meta: {
						uuid,
					},
					payload: {
						channels: channelsToAdd,
					},
				};
				cmd?.action(actions.joinChannels(fsaInteropJoinChannels));
			});
		})
		// Piggyback on resolver's leaveChannels action by unlinking channels here -- this is the only place channel state is directly unlinked in this module.
		// Firing actions in two different modules is built into the redux code. All of the reducers for an action (leaveChannels in this case) are triggered in the order in which they were registered.
		.addCase(leaveChannels, (state, action: PayloadAction<InteropLeaveChannels>, cmd) => {
			const fsa = action.payload;
			const { windowName } = state.resolver.clients[fsa.meta.uuid];
			cleanupConnectIdMap(state.linker);

			const { channels: channelsToRemove } = fsa.payload;

			const oldLinkedChannels = state.linker.clients[windowName]?.joinedChannels ?? [];
			const newLinkedChannels = (state.linker.clients[windowName].joinedChannels = channelsToRemove
				? oldLinkedChannels.filter((name) => !channelsToRemove.includes(name))
				: []);
			uuidsFromWindowName({ clients: state.resolver.clients, windowName }).forEach((uuid) => {
				state.resolver.clients[uuid].joinedChannels = newLinkedChannels;
			});
			cleanupConnectIdMap(state.linker);

			testPoint.triggerSnapshot("LinkerLeaveChannelsResult", { clients: unImmer(state.linker.clients) });

			cmd?.run(setComponentState, {
				args: [{ field: "Interop-#Linker-linkedChannels", value: newLinkedChannels, windowName }],
			});
		})
		// Here channels are unlinked by the end user. The affect must be the same as when channels are left though FDC3 API, so invoke leaveChannels.
		// Invoking leaveChannels, will trigger two actions: 1) the leaveChannels in this module immediately above; 2) the leaveChannels in the resolver module.
		.addCase(actions.unlinkChannels, (state, action: PayloadAction<InteropUnlinkChannels>, cmd) => {
			const fsa = action.payload;
			const { windowName } = fsa.meta;
			const { channels: channelsToRemove } = fsa.payload;

			// Typically just one uuid per window name, but keeping code flexible for future.
			uuidsFromWindowName({ clients: state.resolver.clients, windowName }).forEach((uuid) => {
				const fsaInteropLeaveChannels: InteropLeaveChannels = {
					type: "leaveChannels",
					meta: {
						uuid,
					},
					payload: {
						channels: channelsToRemove,
					},
				};
				cmd?.action(actions.leaveChannels(fsaInteropLeaveChannels));
			});
		})
		/**
		 * The resolver will dispatch `clientAdded` after it has added a new client (uuid).
		 * We respond to this by looking up the linker state for that client.
		 */
		.addCase(clientAdded, (state, action: PayloadAction<ClientAdded>, cmd) => {
			const { windowName, config, connectId, spawnedFromWorkspace } = action.payload;
			if (config.interop?.useLinker) {
				state.linker.clients[windowName] = {
					connectId,
					spawnedFromWorkspace: spawnedFromWorkspace ?? false,
					joinedChannels: [],
				};
				cmd?.run(getLinkerState, {
					args: [windowName],
					successActionCreator: actions.initializeWindow,
				});
			}
		})
		.addCase(clientRemoved, (state, action: PayloadAction<ClientRemoved>) => {
			const { windowName } = action.payload;
			delete state.linker.clients[windowName];
			cleanupConnectIdMap(state.linker);
		});
};

export default {
	getInitialState,
	addReducerCases,
	actions,
	initialize,
};
